<template>
    <pendo-multiselect
        ref="multiselect"
        class="multiselect-filter-accounts"
        :value="selectedAccount"
        :options="accountOptions"
        :internal-search="false"
        :loading="_loading"
        :disabled="disabled"
        :placeholder="placeholder"
        :aria-label="ariaLabel || $t('Select Account')"
        searchable
        label-key="text"
        value-key="id"
        :full-width="fullWidth"
        :min-menu-width="minMenuWidth"
        :max-menu-width="maxMenuWidth"
        :max-menu-height="400"
        :max-trigger-width="maxTriggerWidth"
        :allow-empty="allowEmpty"
        @highlighted-option-change="highlightedOptionChange"
        @input-value-change="searchHandler"
        @select="selectAccount">
        <template
            #trigger
            v-if="useDataMeasureTrigger">
            <pendo-data-measure-trigger
                :aria-label="ariaLabel || $t('Select Account')" />
        </template>
        <template #option="{ option }">
            <span
                v-pendo-tooltip="{
                    content: findNameInMetadata(option),
                }"
                :style="{
                    lineHeight: '26px',
                    display: 'block',
                    width: '100%',
                }">
                {{ option.text }}
            </span>
        </template>
        <template #aside>
            <account-visitor-side-panel
                v-if="shouldShowAsidePanel"
                :id="activeId"
                kind="account"
                :name="activeIdName" />
        </template>
    </pendo-multiselect>
</template>

<script>
import { useWindowSize } from '@vueuse/core';
import { getResponsiveMenuWidth, shouldShowAsidePanel as shouldShowAsidePanelUtil } from '@/utils/responsive-multiselect.utils';
import AccountVisitorSidePanel from '@/common/account-visitor-side-panel/account-visitor-side-panel.vue';
import { fetchAccountById, fetchAccounts } from '@/utils/accounts';
import { PendoDataMeasureTrigger, PendoMultiselect, PendoTooltip } from '@pendo/components';
import { mapActions, mapState } from 'vuex';
import { getAllAccountsOption, isParentAccount } from './util';
const MAX_RESULTS = 100;

const isDisplayableValue = (val) => {
    return !!val && ['string', 'number'].includes(typeof val);
};

export default {
    name: 'AccountChooser',
    components: {
        PendoMultiselect,
        PendoDataMeasureTrigger,
        AccountVisitorSidePanel,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        account: {
            type: [Object, String],
            default: undefined,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        useDataMeasureTrigger: {
            type: Boolean,
            default: false,
        },
        maxTriggerWidth: {
            type: Number,
            default: 368,
        },
        allowEmpty: {
            type: Boolean,
            default: false,
        },
        fullWidth: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        ariaLabel: {
            type: [String, null],
            default: null,
        },
    },
    emits: ['select-account'],
    setup () {
        const { width: windowWidth } = useWindowSize();

        return {
            windowWidth,
        };
    },
    data () {
        return {
            activeId: '',
            accountOptions: [],
            allAccountsOption: {},
            searchMoreText: '',
            initialLoad: true,
            searchTerm: '',
            shouldToggleMenu: false,
        };
    },
    computed: {
        ...mapState({
            accountDisplayName: (state) => state.subscription.settings.accountDisplayName,
        }),
        minMenuWidth () {
            return getResponsiveMenuWidth(this.windowWidth);
        },
        maxMenuWidth () {
            return getResponsiveMenuWidth(this.windowWidth);
        },
        shouldShowAsidePanel () {
            return shouldShowAsidePanelUtil(this.windowWidth);
        },
        selectedAccount () {
            return this.account;
        },
        _loading () {
            return this.loading || this.initialLoad;
        },
        placeholder () {
            return this.allowEmpty ? this.$t('Select Account') : this.$t('All Accounts');
        },
        activeIdName () {
            return this.accountOptions.find((account) => account.id === this.activeId)?.accountDisplayName;
        },
    },
    watch: {
        account: {
            async handler (val) {
                if (val) {
                    await this.loadSelectedAccount();
                }
            },
        },
    },
    async created () {
        this.accountOptions = [{ ...getAllAccountsOption() }];
        this.allAccountsOption = { ...getAllAccountsOption() };
        this.searchMoreText = this.$t('accountChooser.Showing first {maxResults} results. Search for more.', {
            maxResults: MAX_RESULTS,
        });
        await this.init();
    },
    methods: {
        ...mapActions({
            loadSetting: 'subscription/loadSetting',
        }),
        async init () {
            this.initialLoad = true;
            await this.initAccountOptions();
            if (this.loading) {
                // if loading, watch for set to false then run load acct logic
                const unwatch = this.$watch('loading', (loading) => {
                    if (!loading) {
                        this.loadSelectedAccount();
                        unwatch();
                        if(this.shouldToggleMenu) {
                            this.shouldToggleMenu = false;
                            setTimeout(() => {
                                this.toggleMenu();
                            }, 0);
                        }
                    }
                });
            } else {
                this.loadSelectedAccount();
                if(this.shouldToggleMenu) {
                    this.shouldToggleMenu = false;
                    setTimeout(() => {
                        this.toggleMenu();
                    }, 0);
                }
            }
        },
        async initAccountOptions () {
            await this.loadSetting({ setting: 'accountDisplayName', retrieveMetadata: false });
            const accountResult = await this.searchAllAccounts();

            if (accountResult.length) {
                accountResult[0].divided = true;
            }

            this.accountOptions = [{ ...getAllAccountsOption() }, ...accountResult];
        },
        async loadSelectedAccount () {
            // if display name has changed from what's stored in selectedAccounted on filters, update
            if (this.account) {
                // allow for account to be passed as object with an id key or string
                const accountId = this.account?.id ?? this.account;

                let selectedAccountOption = this.accountOptions.find((x) => x.id === accountId);

                if (!selectedAccountOption) {
                    try {
                        // if an account is selected but was not included in the options list, attempt to fetch it
                        const account = await fetchAccountById(accountId);
                        selectedAccountOption = this.mapOption(account);
                        this.accountOptions.push(selectedAccountOption);
                    } catch {
                        // if fails (for ex if no longer exists), will be reset back to all accounts
                        this.selectAccount(this.allAccountsOption);
                    }
                }

                if (selectedAccountOption && selectedAccountOption.text !== this.account?.text) {
                    this.selectAccount(selectedAccountOption);
                }
            } else if (!this.account && !this.allowEmpty) {
                this.selectAccount(this.allAccountsOption);
            }

            this.initialLoad = false;
        },
        getAccountOptions (term = '') {
            return term.length === 0 ? this.searchAllAccounts() : this.searchForTerm(term);
        },
        searchAllAccounts () {
            return this.search().then((results) => {
                return this.processInitialResults(results);
            });
        },
        processInitialResults (results) {
            if (this.tooManyResults(results.data)) {
                const trimmed = this.toOptions(results.data).slice(0, MAX_RESULTS);

                return this.addMessage(trimmed, this.searchMoreText);
            }

            return this.toOptions(results.data);
        },
        searchForTerm (term) {
            return this.search(term).then((results) => {
                return results.data.length
                    ? this.addSearchMessage(this.toOptions(results.data))
                    : this.noResultsOptions();
            });
        },
        search (term) {
            return fetchAccounts(term);
        },
        mapOption (account) {
            return {
                id: account.id,
                text: this.buildText(account),
                isParentAccount: isParentAccount(account),
                metadata: account.metadata,
                accountDisplayName: this.accountNameForSidePanel(account),
            };
        },
        toOptions (accounts) {
            return accounts.map((account) => this.mapOption(account));
        },
        findNameInMetadata (account) {
            if (!this.searchTerm) {
                return;
            }

            const foundAccount = this.accountOptions.find((x) => x.id === account.id);
            let metadata = foundAccount?.metadata;
            if (!metadata) {
                metadata = {};
            }

            let contentKey;
            let contentValue = '';
            Object.keys(metadata).every((key) => {
                return Object.keys(metadata[key]).every((subkey) => {
                    const value = metadata[key][subkey];
                    if (typeof value === 'string' && value.toLowerCase().indexOf(this.searchTerm.toLowerCase()) === 0) {
                        contentKey = subkey;
                        contentValue = value;

                        return false;
                    }

                    return true;
                });
            });

            return contentValue === account.id || contentValue === this.getAccountDisplayName(metadata)
                ? ''
                : `${contentKey}: ${contentValue}`;
        },
        buildText (account) {
            const { id, metadata } = account;
            const appDisplayName = this.getAccountDisplayName(metadata);

            return !appDisplayName || String(id).toLowerCase() === String(appDisplayName).toLowerCase()
                ? id
                : `${id} (${appDisplayName})`;
        },
        addSearchMessage (list) {
            return this.tooManyResults(list) ? this.addMessage(list, this.searchMoreText) : list;
        },
        noResultsOptions () {
            return this.addMessage([], this.$t('accountChooser.No matches found'));
        },
        addMessage (list, message) {
            return [
                ...list,
                {
                    text: message,
                    id: 'account-chooser-dropdown-message',
                    disabled: true,
                },
            ];
        },
        tooManyResults (results) {
            return results.length >= MAX_RESULTS;
        },
        searchHandler (query) {
            this.searchTerm = query;
            this.getAccountOptions(query).then((options) => {
                options[0].divided = true;
                this.accountOptions = [this.allAccountsOption, ...options];
            });
        },
        selectAccount (selectedOption) {
            if (selectedOption) this.$emit('select-account', selectedOption);
        },
        getAccountDisplayName (metadata) {
            if (this.accountDisplayName?.value && metadata) {
                const [group, field] = this.accountDisplayName.value.split('.');
                const accountDisplayName = metadata[group]?.[field];
                if (isDisplayableValue(accountDisplayName)) return accountDisplayName;
            }

            return undefined;
        },
        accountNameForSidePanel (account) {
            const { id, metadata } = account;
            const appDisplayName = this.getAccountDisplayName(metadata);

            return appDisplayName || id;
        },
        highlightedOptionChange (option) {
            if (option) {
                if (option.id === 'account-chooser-dropdown-message') {
                    this.activeId = 'NO_RESULTS';
                } else {
                    this.activeId = option.id;
                }
            }
        },
        /** @public */
        toggleMenu () {
            if(this._loading) {
                this.shouldToggleMenu = true;
            } else {
                this.$refs.multiselect.toggleMenu();
            }
        },
    },
};
</script>
